import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@frontegg/react';

/**
 * URL param names
 */
const URL_PARAM_FILTERS = 'filters';
const URL_PARAM_SORT = 'sort';
const URL_PARAM_VIEW_STATE = 'viewState';

/**
 * Source of the current filter values
 */
export type FilterSource = 'USER' | 'BACKEND_VIEW' | 'DEFAULT';

export type PersistedFiltersConfig<
  TFilters extends Record<string, unknown>,
  TSort extends Record<string, unknown>,
  TViewState extends Record<string, unknown>,
> = {
  /**
   * Unique key to identify this filter set (e.g., 'appointments', 'tasks', 'calls')
   */
  viewId: string;

  /**
   * Default filter values when no persisted state exists
   */
  defaultFilters: TFilters;

  /**
   * Default sort configuration
   */
  defaultSort: TSort;

  /**
   * Default view state values when no persisted state exists
   */
  defaultViewState?: TViewState;

  /**
   * Whether to persist filters to localStorage (default: true)
   */
  persistToStorage?: boolean;

  /**
   * Whether to sync filters with URL params (default: true)
   */
  syncWithUrl?: boolean;

  // /**
  //  * Backend saved view (reactive prop - pass from your API hook).
  //  * Used when user hasn't set any filters.
  //  */
  // backendView?: {
  //   filters?: TFilters;
  //   sort?: TSort;
  // } | null;
};

/**
 * Get the org-scoped storage key
 */
const getStorageKey = ({
  viewId,
  orgId,
  key,
}: {
  viewId: string;
  orgId?: string;
  key: string;
}): string => {
  if (!orgId) {
    return `ih:${viewId}:${key}`;
  }
  return `ih:${orgId}:${viewId}:${key}`;
};

/**
 * Safely parse JSON
 */
const safeJsonParse = <T>(value: string | null, fallback: T): T => {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

/**
 * Read from localStorage
 */
const readFromStorage = <T extends Record<string, unknown>>({
  key,
}: {
  key: string;
}): T | undefined => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return undefined;
    return safeJsonParse<T | undefined>(stored, undefined);
  } catch {
    return undefined;
  }
};

/**
 * Write to localStorage
 */
const writeToStorage = <T extends Record<string, unknown>>({
  key,
  data,
}: {
  key: string;
  data: T;
}): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data as T));
  } catch {
    // Silently fail
  }
};

/**
 * Clear from localStorage
 */
const clearFromStorage = ({ key }: { key: string }): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Silently fail
  }
};

/**
 * Parse from URL
 */
const parseFromUrl = <T extends Record<string, unknown>>(
  search: string,
  paramKey: string
): T | undefined => {
  try {
    const params = new URLSearchParams(search);
    const paramValue = params.get(paramKey);
    if (!paramValue) return undefined;

    return JSON.parse(paramValue) as T;
  } catch {
    return undefined;
  }
};

/**
 * Compare two objects for equality.
 */
export const areObjectsEqual = <T extends Record<string, unknown>>(
  a: T | undefined = {} as T,
  b: T | undefined = {} as T,
  keys?: (keyof T)[]
): boolean => {
  const keysToCompare =
    keys ??
    ([...new Set([...Object.keys(a), ...Object.keys(b)])] as (keyof T)[]);

  return keysToCompare.every((key) => {
    const valA = a[key];
    const valB = b[key];

    if (valA === undefined && valB === undefined) return true;
    return JSON.stringify(valA) === JSON.stringify(valB);
  });
};

export const usePersistedFilters = <
  TFilters extends Record<string, unknown>,
  TSort extends Record<string, unknown>,
  TViewState extends Record<string, unknown>,
>(
  config: PersistedFiltersConfig<TFilters, TSort, TViewState>
) => {
  const {
    viewId,
    defaultFilters,
    defaultSort,
    defaultViewState,
    persistToStorage = true,
    syncWithUrl = true,
    // backendView,
  } = config;

  const { user } = useAuth();
  const orgId = user?.tenantId;
  const navigate = useNavigate();
  const location = useLocation();

  const locationRef = useRef(location);
  locationRef.current = location;

  const getInitialFilters = useCallback(() => {
    let filters;
    if (syncWithUrl) {
      filters = parseFromUrl<TFilters>(
        locationRef.current.search,
        URL_PARAM_FILTERS
      );
      if (filters) {
        return filters;
      }
    }

    if (persistToStorage) {
      filters = readFromStorage<TFilters>({
        key: getStorageKey({ viewId, orgId, key: URL_PARAM_FILTERS }),
      });
    }

    return filters;
  }, [syncWithUrl, persistToStorage, viewId, orgId]);

  const getInitialSort = useCallback(() => {
    let sort;
    if (syncWithUrl) {
      sort = parseFromUrl<TSort>(locationRef.current.search, URL_PARAM_SORT);
      if (sort) {
        return sort;
      }
    }
    if (persistToStorage) {
      sort = readFromStorage<TSort>({
        key: getStorageKey({ viewId, orgId, key: URL_PARAM_SORT }),
      });
    }
    return sort;
  }, [syncWithUrl, persistToStorage, viewId, orgId]);

  const getInitialViewState = useCallback(() => {
    let viewState;
    if (syncWithUrl) {
      viewState = parseFromUrl<TViewState>(
        locationRef.current.search,
        URL_PARAM_VIEW_STATE
      );
      if (viewState) {
        return viewState;
      }
    }
    if (persistToStorage) {
      viewState = readFromStorage<TViewState>({
        key: getStorageKey({ viewId, orgId, key: URL_PARAM_VIEW_STATE }),
      });
    }
    return viewState;
  }, [syncWithUrl, persistToStorage, viewId, orgId]);

  const [filtersState, setFiltersState] = useState<TFilters | undefined>(
    getInitialFilters()
  );
  const [sortState, setSortState] = useState<TSort | undefined>(
    getInitialSort()
  );
  const [viewStateState, setViewStateState] = useState<TViewState | undefined>(
    getInitialViewState()
  );

  /**
   * Derive final values: userState > backendView > defaults
   * NO MERGING - winner takes all
   */

  const filters = useMemo(() => {
    return filtersState ?? defaultFilters;
  }, [filtersState, defaultFilters]);
  const sort = useMemo(() => {
    return sortState ?? defaultSort;
  }, [sortState, defaultSort]);

  const viewState = useMemo(() => {
    return viewStateState ?? defaultViewState;
  }, [viewStateState, defaultViewState]);

  /**
   * Sync URL
   */
  const syncUrl = useCallback(
    (state: { filters?: TFilters; sort?: TSort; viewState?: TViewState }) => {
      if (!syncWithUrl) return;

      const keyMap = {
        filters: URL_PARAM_FILTERS,
        sort: URL_PARAM_SORT,
        viewState: URL_PARAM_VIEW_STATE,
      };

      const defaultValues = {
        filters: defaultFilters,
        sort: defaultSort,
        viewState: defaultViewState,
      };

      const searchParams = new URLSearchParams(locationRef.current.search);
      Object.entries(state).forEach(([key, value]) => {
        if (
          !value ||
          areObjectsEqual(
            value,
            defaultValues[key as keyof typeof defaultValues]
          )
        ) {
          searchParams.delete(keyMap[key as keyof typeof keyMap]);
          return;
        }
        searchParams.set(
          keyMap[key as keyof typeof keyMap],
          JSON.stringify(value)
        );
      });
      navigate(`${locationRef.current.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    },
    [
      syncWithUrl,
      navigate,
      locationRef,
      defaultFilters,
      defaultSort,
      defaultViewState,
    ]
  );

  const syncStorage = useCallback(
    (state: { filters?: TFilters; sort?: TSort; viewState?: TViewState }) => {
      if (!persistToStorage) return;

      const { filters, sort, viewState } = state;

      if (filters && !areObjectsEqual(filters, defaultFilters)) {
        writeToStorage({
          key: getStorageKey({ viewId, orgId, key: URL_PARAM_FILTERS }),
          data: filters,
        });
      } else {
        clearFromStorage({
          key: getStorageKey({ viewId, orgId, key: URL_PARAM_FILTERS }),
        });
      }

      if (sort && !areObjectsEqual(sort, defaultSort)) {
        writeToStorage({
          key: getStorageKey({ viewId, orgId, key: URL_PARAM_SORT }),
          data: sort,
        });
      } else {
        clearFromStorage({
          key: getStorageKey({ viewId, orgId, key: URL_PARAM_SORT }),
        });
      }

      if (viewState && !areObjectsEqual(viewState, defaultViewState)) {
        writeToStorage({
          key: getStorageKey({ viewId, orgId, key: URL_PARAM_VIEW_STATE }),
          data: viewState,
        });
      } else {
        clearFromStorage({
          key: getStorageKey({ viewId, orgId, key: URL_PARAM_VIEW_STATE }),
        });
      }
    },
    [
      persistToStorage,
      viewId,
      orgId,
      defaultFilters,
      defaultSort,
      defaultViewState,
    ]
  );

  /**
   * Set filters (replaces entirely)
   */
  const setFilters = useCallback((newFilters: TFilters) => {
    setFiltersState(newFilters);
  }, []);

  /**
   * Set sort (replaces entirely)
   */
  const setSort = useCallback((newSort: TSort) => {
    setSortState(newSort);
  }, []);

  /**
   * Set view state (replaces entirely)
   */
  const setViewState = useCallback((newViewState: TViewState) => {
    setViewStateState(newViewState);
  }, []);

  /**
   * Update filters (merges with current)
   */
  const updateFilters = useCallback(
    (partialFilters: Partial<TFilters>) => {
      const newFilters = { ...filters, ...partialFilters } as TFilters;
      setFilters(newFilters);
    },
    [filters, setFilters]
  );

  /**
   * Update sort (merges with current)
   */
  const updateSort = useCallback(
    (partialSort: Partial<TSort>) => {
      const newSort = { ...sort, ...partialSort } as TSort;
      setSort(newSort);
    },
    [sort, setSort]
  );
  /**
   * Update view state (merges with current)
   */
  const updateViewState = useCallback(
    (partialViewState: Partial<TViewState>) => {
      const newViewState = { ...viewState, ...partialViewState } as TViewState;
      setViewState(newViewState);
    },
    [viewState, setViewState]
  );

  /**
   * Reset filters only - keeps current sort, resets filters to fallback
   */
  const resetFilters = useCallback(() => {
    setFiltersState(defaultFilters ?? undefined);
  }, [defaultFilters]);

  /**
   * Reset sort only - keeps current filters, resets sort to fallback
   */
  const resetSort = useCallback(() => {
    setSortState(defaultSort ?? undefined);
  }, [defaultSort]);

  /**
   * Reset view state only - keeps current filters and sort, resets view state to fallback
   */
  const resetViewState = useCallback(() => {
    setViewStateState(defaultViewState ?? undefined);
  }, [defaultViewState]);

  /**
   * Reset all - clears user state entirely, falls back to backendView or defaults
   */
  const resetAll = useCallback(() => {
    // clearFromStorage(viewId, orgId);
    resetFilters();
    resetSort();
    resetViewState();
  }, [resetFilters, resetSort, resetViewState]);

  /**
   * Has filters applied (differs from defaults)
   */
  const hasDefaultFiltersApplied = useMemo(() => {
    return areObjectsEqual(filters, defaultFilters);
  }, [filters, defaultFilters]);

  /**
   * Has sort applied (differs from defaults)
   */
  const hasDefaultSortApplied = useMemo(
    () => areObjectsEqual(sort, defaultSort),
    [sort, defaultSort]
  );
  /**
   * Has view state applied (differs from defaults)
   */
  const hasDefaultViewStateApplied = useMemo(
    () => areObjectsEqual(viewState, defaultViewState),
    [viewState, defaultViewState]
  );

  /**
   * Handle browser navigation
   */
  useEffect(() => {
    syncUrl({
      filters,
      sort,
      viewState,
    });
    syncStorage({ filters, sort, viewState });
  }, [filters, sort, viewState]);

  /**
   * Re-init on org change
   */
  const prevOrgId = useRef(orgId);
  useEffect(() => {
    if (prevOrgId.current !== orgId) {
      prevOrgId.current = orgId;
      setFiltersState(getInitialFilters());
      setSortState(getInitialSort());
      setViewStateState(getInitialViewState());
    }
  }, [orgId]);

  return {
    filters,
    sort,
    viewState,
    setFilters,
    setSort,
    setViewState,
    updateFilters,
    updateSort,
    updateViewState,
    resetFilters,
    resetSort,
    resetViewState,
    resetAll,
    hasDefaultFiltersApplied,
    hasDefaultSortApplied,
    hasDefaultViewStateApplied,
    areObjectsEqual,
  };
};
